import { toast } from '@ixigo/iui/Toast';
import { useElementOnScreen } from '@repo/shared/hooks';
import { useEffect, useState } from 'react';
import {
	type FormValidationResult,
	NavigationHeader,
	SearchForm,
	type SearchFormData,
} from '@/components';
import { CabSelectionWithData } from '@/components/cab-selection';
import {
	loadSearchFormData,
	SearchBar,
	saveSearchFormData,
	searchTracker,
	useSearchFormData,
} from '@/components/search-form';
import { useSearchFormActions } from '@/components/search-form/store';
import { TrackerProvider } from '@/lib/analytics/provider';
import { useNavigate } from '@/lib/navigation';
import { serializeRouteParams } from '@/utils/routeParams';
import { isAfterMinimumDuration } from '../-utils';
import { SearchBanner } from './search-banner';

const CAB_SEARCH_FORM_SELECTOR = '#cab-search-form';
const CAB_SEARCH_SECTION_SELECTOR = '#cab-search-section';

export const SearchSection = () => {
	const navigate = useNavigate();
	const searchFormActions = useSearchFormActions();
	const [cabSelectionData, setCabSelectionData] =
		useState<Required<SearchFormData> | null>(null);
	const searchFormData = useSearchFormData();

	// Load saved search form data on mount
	useEffect(() => {
		const savedFormData = loadSearchFormData();
		if (savedFormData) {
			searchFormActions.updateFormData({
				...savedFormData,
				// if the date time is after 30 minutes, then set it to 3 hour from now
				dateTime: isAfterMinimumDuration(savedFormData.dateTime, 30)
					? savedFormData.dateTime
					: new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString(),
			});
		}
	}, [searchFormActions]);

	const isCabSearchFormOnScreen = useElementOnScreen(
		CAB_SEARCH_FORM_SELECTOR,
		CAB_SEARCH_SECTION_SELECTOR,
		{
			threshold: 0.25,
		},
	);

	const handleBackClick = () => {
		navigate({
			back: 'exit',
			href: '/',
			replace: true,
			reloadDocument: true,
		});
	};

	const handleSubmit = async (validationResult: FormValidationResult) => {
		if (!validationResult.isValid) {
			toast.error(validationResult.errorMessage, {
				position: 'bottom-center',
				duration: 2000,
			});
			return;
		}
		saveSearchFormData(validationResult.data);
		setCabSelectionData(validationResult.data);
	};

	const handleSelectionClose = () => {
		setCabSelectionData(null);
	};

	const handleSelectionConfirm = (cabId: string) => {
		// for the routeParams, we only need
		const routeParams = serializeRouteParams({
			...{
				...searchFormData,
				pickup: {
					lat: searchFormData.pickup?.lat,
					lng: searchFormData.pickup?.lng,
					placeId: searchFormData.pickup?.placeId,
				},
				drop: {
					lat: searchFormData.drop?.lat,
					lng: searchFormData.drop?.lng,
					placeId: searchFormData.drop?.placeId,
				},
			},
			cabId,
		});
		navigate({
			to: '/review',
			search: routeParams,
			viewTransition: {
				types: ['slide-left'],
			},
		});
	};

	const handleSearchClick = () => {
		document.getElementById('cab-search-section')?.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
		});
	};

	return (
		<TrackerProvider track={searchTracker.track}>
			<div
				className="flex flex-col relative bg-primary p-20"
				id="cab-search-section"
			>
				<div
					className={`fixed top-0 left-0 z-10 bg-primary w-full transition-[border,box-shadow,transform] duration-300 ease-out ${
						!isCabSearchFormOnScreen
							? 'border-b border-tertiary shadow-100 translate-y-0'
							: 'border-b-none shadow-none -translate-y-full'
					}`}
				>
					<NavigationHeader
						title="Airport Cabs"
						leftAction={handleBackClick}
						className="relative"
					/>
					<div className="px-20 pb-10">
						<SearchBar
							searchValue={searchFormData.pickup?.mainText}
							onSearchClick={handleSearchClick}
						/>
					</div>
				</div>
				<SearchBanner onBack={handleBackClick} />
				<SearchForm
					onSubmit={handleSubmit}
					id="cab-search-form"
					className="relative mt-100px z-[1] bg-primary rounded-20"
				/>
				<CabSelectionWithData
					showOptions={!!cabSelectionData}
					onClose={handleSelectionClose}
					onConfirm={handleSelectionConfirm}
					formData={cabSelectionData}
				/>
			</div>
		</TrackerProvider>
	);
};
